import {
  JsonSchemaProperty,
  GenericStringObject,
  RowData,
  RowDataErrors,
  RowDataReducerActionPayload,
  RowDataReducerState,
  RowDataValueType,
} from "../../../types";
import { validateRowDataInput } from "../../../pages/LandingPage/validations";

export const removeMultiValuedFieldAction = (
  payload: Partial<RowDataReducerActionPayload>,
  state: RowDataReducerState,
  property: JsonSchemaProperty,
): RowDataReducerState => {
  const {
    propertyKey,
    newFieldValue,
    rowIndex,
    selectedTabSectionKey,
    propertySubKey,
    nestedPropertySubKey,
    nestedRowIndex,
  } = payload;
  if (!propertyKey) return state;

  if (nestedRowIndex !== undefined && nestedPropertySubKey) {
    if (selectedTabSectionKey) {
      return removeTabbedNestedMultiValuedField(
        propertyKey,
        propertySubKey,
        nestedPropertySubKey,
        nestedRowIndex,
        newFieldValue,
        rowIndex,
        property,
        selectedTabSectionKey,
        state,
      );
    }

    return removeNestedMultiValuedFieldAtIndex(
      propertyKey,
      propertySubKey,
      nestedPropertySubKey,
      nestedRowIndex,
      newFieldValue,
      rowIndex,
      property,
      state,
    );
  }

  if (nestedRowIndex === undefined && nestedPropertySubKey) {
    return removeObjectMultiValuedField(
      propertyKey,
      propertySubKey,
      newFieldValue,
      rowIndex,
      property,
      selectedTabSectionKey,
      state,
    );
  }

  if (selectedTabSectionKey) {
    return removeNestedMultiValuedField(
      propertyKey,
      newFieldValue,
      rowIndex,
      property,
      selectedTabSectionKey,
      state,
    );
  }

  return removeMultiValuedField(
    propertyKey,
    newFieldValue,
    rowIndex,
    property,
    state,
  );
};

const removeObjectMultiValuedField = (
  propertyKey: string,
  propertySubKey: string | undefined,
  newFieldValue: RowDataValueType | undefined,
  rowIndex: number | undefined,
  property: JsonSchemaProperty,
  selectedTabSectionKey: string | undefined,
  state: RowDataReducerState,
): RowDataReducerState => {
  if (propertySubKey === undefined) return state;

  const container = selectedTabSectionKey
    ? (state.rowData[selectedTabSectionKey] as RowData) || {}
    : state.rowData;

  const parentObject = (container[propertyKey] as RowData) || {};

  const currentValue = parentObject[propertySubKey];

  // Ensure the current value is an array
  const currentArray = Array.isArray(currentValue) ? currentValue : [];

  // Remove the selected value using its index
  const updatedArray =
    rowIndex !== undefined
      ? currentArray.filter((_, index) => index !== rowIndex)
      : newFieldValue === null
        ? []
        : currentArray;

  const updatedParentObject = {
    ...parentObject,
    [propertySubKey]: updatedArray,
  };

  const updatedContainer = {
    ...container,
    [propertyKey]: updatedParentObject,
  } as RowData;

  const updatedRowData = selectedTabSectionKey
    ? {
        ...state.rowData,
        [selectedTabSectionKey]: updatedContainer,
      }
    : updatedContainer;

  return {
    ...state,
    rowData: updatedRowData,
    rowDataErrors: {
      ...state.rowDataErrors,
      [propertyKey]: validateRowDataInput(
        propertyKey,
        property,
        updatedArray as RowDataValueType,
        state,
      ),
    },
  };
};

const removeNestedMultiValuedFieldAtIndex = (
  propertyKey: string,
  propertySubKey: string | undefined,
  nestedPropertySubKey: string,
  nestedRowIndex: number,
  newFieldValue: RowDataValueType | undefined,
  rowIndex: number | undefined,
  property: JsonSchemaProperty,
  state: RowDataReducerState,
): RowDataReducerState => {
  const parentArray = (state.rowData[propertyKey] as RowData[]) || [];
  const isKeyedNestedArray = nestedPropertySubKey !== propertySubKey;
  const containerKey = isKeyedNestedArray
    ? propertySubKey
    : nestedPropertySubKey || propertySubKey;
  if (!containerKey) return state;

  const updatedParentArray = parentArray.map((item, index) => {
    if (index !== nestedRowIndex) return item;

    const row = (item as RowData) || {};
    const currentNestedArray = (row[containerKey] as RowDataValueType[]) || [];

    let updatedNestedArray: RowDataValueType[];
    if (typeof rowIndex !== "undefined") {
      updatedNestedArray = currentNestedArray.filter((_, i) => i !== rowIndex);
    } else if (newFieldValue === null) {
      updatedNestedArray = [];
    } else {
      updatedNestedArray = currentNestedArray;
    }

    return {
      ...row,
      [containerKey]: updatedNestedArray,
    } as RowData;
  });

  const updatedRowData = {
    ...state.rowData,
    [propertyKey]: updatedParentArray,
  } as RowData;

  const updatedRowDataErrors = {
    ...state.rowDataErrors,
    [propertyKey]: validateRowDataInput(
      propertyKey,
      property,
      updatedParentArray as RowDataValueType,
      state,
    ),
  } as RowDataErrors;

  return {
    ...state,
    rowData: updatedRowData,
    rowDataErrors: updatedRowDataErrors,
  };
};

const removeTabbedNestedMultiValuedField = (
  propertyKey: string,
  propertySubKey: string | undefined,
  nestedPropertySubKey: string,
  nestedRowIndex: number,
  newFieldValue: RowDataValueType | undefined,
  rowIndex: number | undefined,
  property: JsonSchemaProperty,
  selectedTabSectionKey: string,
  state: RowDataReducerState,
): RowDataReducerState => {
  const tabData = (state.rowData[selectedTabSectionKey] as RowData) || {};
  const parentArray = (tabData[propertyKey] as RowData[]) || [];
  const isKeyedNestedArray = nestedPropertySubKey !== propertySubKey;
  const containerKey = isKeyedNestedArray
    ? propertySubKey
    : nestedPropertySubKey || propertySubKey;
  if (!containerKey) return state;

  const updatedParentArray = parentArray.map((item, index) => {
    if (index !== nestedRowIndex) return item;

    const row = (item as RowData) || {};
    const currentNestedArray = (row[containerKey] as RowDataValueType[]) || [];

    let updatedNestedArray: RowDataValueType[];
    if (typeof rowIndex !== "undefined") {
      updatedNestedArray = currentNestedArray.filter((_, i) => i !== rowIndex);
    } else if (newFieldValue === null) {
      updatedNestedArray = [];
    } else {
      updatedNestedArray = currentNestedArray;
    }

    return {
      ...row,
      [containerKey]: updatedNestedArray,
    } as RowData;
  });

  const updatedRowData = {
    ...state.rowData,
    [selectedTabSectionKey]: {
      ...tabData,
      [propertyKey]: updatedParentArray,
    },
  } as RowData;

  const updatedRowDataErrors = {
    ...state.rowDataErrors,
    [propertyKey]: validateRowDataInput(
      propertyKey,
      property,
      updatedParentArray as RowDataValueType,
      state,
    ),
  } as RowDataErrors;

  return {
    ...state,
    rowData: updatedRowData,
    rowDataErrors: updatedRowDataErrors,
  };
};

const removeNestedMultiValuedField = (
  propertyKey: string,
  newFieldValue: RowDataValueType | undefined,
  rowIndex: number | undefined,
  property: JsonSchemaProperty,
  selectedTabSectionKey: string,
  state: RowDataReducerState,
): RowDataReducerState => {
  const currentArray =
    ((state.rowData[selectedTabSectionKey] as RowData)?.[
      propertyKey
    ] as GenericStringObject[]) || [];

  let updatedArray: GenericStringObject[];
  if (typeof rowIndex !== "undefined") {
    updatedArray = currentArray.filter((_, index) => index !== rowIndex);
  } else if (newFieldValue === null) {
    updatedArray = [];
  } else {
    updatedArray = currentArray;
  }
  const updatedRowData = {
    ...state.rowData,
    [selectedTabSectionKey]: {
      ...(state.rowData[selectedTabSectionKey] as RowData),
      [propertyKey]: updatedArray,
    },
  } as RowData;

  const updatedRowDataErrors = {
    ...state.rowDataErrors,
    [propertyKey]: validateRowDataInput(
      propertyKey,
      property,
      updatedArray,
      state,
    ),
  } as RowDataErrors;

  return {
    ...state,
    rowData: updatedRowData,
    rowDataErrors: updatedRowDataErrors,
  };
};

const removeMultiValuedField = (
  propertyKey: string,
  newFieldValue: RowDataValueType | undefined,
  rowIndex: number | undefined,
  property: JsonSchemaProperty,
  state: RowDataReducerState,
): RowDataReducerState => {
  const currentArray =
    (state.rowData[propertyKey] as GenericStringObject[]) || [];

  let updatedArray: GenericStringObject[];
  if (typeof rowIndex !== "undefined") {
    updatedArray = currentArray.filter((_, index) => index !== rowIndex);
  } else if (newFieldValue === null) {
    updatedArray = [];
  } else {
    updatedArray = currentArray;
  }
  const updatedRowData = {
    ...state.rowData,
    [propertyKey]: updatedArray,
  } as RowData;

  const updatedRowDataErrors = {
    ...state.rowDataErrors,
    [propertyKey]: validateRowDataInput(
      propertyKey,
      property,
      updatedArray,
      state,
    ),
  } as RowDataErrors;

  return {
    ...state,
    rowData: updatedRowData,
    rowDataErrors: updatedRowDataErrors,
  };
};
